import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Download, User, AlertTriangle, ExternalLink, AlertCircle } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { instagramService, API_LIMITATIONS } from '../services/socialMediaApi';

interface ProfileData {
  username: string;
  fullName: string;
  biography: string;
  profilePicUrl: string;
  isVerified: boolean;
  followers: number;
  following: number;
  posts: number;
  isPrivate: boolean;
}

const InstagramProfileDownloader: React.FC = () => {
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [downloadQuality, setDownloadQuality] = useState<'hd' | 'sd'>('hd');

  useSEO({
    title: 'Instagram Profile Picture Downloader - Download IG Profile Photos',
    description: 'Download Instagram profile pictures in high quality. Free tool to save Instagram profile photos and view public profile information.',
    keywords: 'instagram profile picture downloader, ig profile photo download, instagram dp download, profile picture saver',
    canonical: 'https://snap-tools.online/social/instagram-profile-downloader'
  });

  // Simulate Instagram profile fetch
  const fetchProfile = async () => {
    if (!username.trim()) {
      toast.error('Please enter a username');
      return;
    }

    const cleanUsername = instagramService.extractUsername(username.trim());
    setIsLoading(true);
    
    try {
      // Show API limitation notice
      toast.error(API_LIMITATIONS.INSTAGRAM, { duration: 8000 });
      
      // For demonstration purposes, show what the interface would look like
      // In production, this would call: await instagramService.getProfileInfo(cleanUsername);
      
      // Demo data structure (what real data would look like)
      const demoProfile: ProfileData = {
        username: cleanUsername,
        fullName: 'Demo Profile (Real API Required)',
        biography: 'This is a demonstration of the interface. Real Instagram API access requires authentication and approval from Instagram.',
        profilePicUrl: 'https://picsum.photos/400/400?random=1',
        isVerified: false,
        followers: 0,
        following: 0,
        posts: 0,
        isPrivate: false
      };

      setProfileData(demoProfile);
      toast('Demo interface shown. Real functionality requires Instagram API authentication.');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Instagram API access requires authentication. See documentation for setup.');
    } finally {
      setIsLoading(false);
    }
  };

  // Download profile picture
  const downloadProfilePicture = async () => {
    if (!profileData) return;

    try {
      // Try multiple download methods for better compatibility
      try {
        // Method 1: Try fetch with proxy
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(profileData.profilePicUrl)}`;
        const response = await fetch(proxyUrl, {
          mode: 'cors',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const link = document.createElement('a');
          const url = window.URL.createObjectURL(blob);
          link.href = url;
          link.download = `${profileData.username}_profile_picture_${downloadQuality}.jpg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          toast.success('Download completed successfully!');
          return;
        }
      } catch (proxyError) {
        console.log('Proxy method failed, trying direct download');
      }
      
      // Method 2: Direct download (fallback)
      const link = document.createElement('a');
      link.href = profileData.profilePicUrl;
      link.download = `${profileData.username}_profile_picture_${downloadQuality}.jpg`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download initiated! If blocked by browser, right-click the image and select "Save image as..."');
      
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Download failed. Please right-click the image and select "Save image as..." to download manually.');
    }
  };

  // Format number
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mb-4">
              <Instagram className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Instagram Profile Picture Downloader</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Download Instagram profile pictures in high quality. View public profile information and save profile photos easily.
            </p>
            
            {/* API Limitation Notice */}
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-center justify-center mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mr-2" />
                <span className="text-amber-800 font-medium">API Limitation Notice</span>
              </div>
              <p className="text-amber-700 text-sm">
                Instagram API requires authentication and business approval. This demo shows the interface structure. 
                For production use, implement Instagram Basic Display API or Graph API with proper credentials.
              </p>
            </div>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Search Form */}
          <div className="bg-gray-50 rounded-lg p-6 mb-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram Username
                </label>
                <div className="flex gap-3">
                  <div className="flex-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 text-sm">@</span>
                    </div>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="username"
                      className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && fetchProfile()}
                    />
                  </div>
                  <button
                    onClick={fetchProfile}
                    disabled={isLoading || !username.trim()}
                    className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>Loading...</>
                    ) : (
                      <>
                        <User className="w-4 h-4" />
                        Search
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Download Quality
                </label>
                <select
                  value={downloadQuality}
                  onChange={(e) => setDownloadQuality(e.target.value as 'hd' | 'sd')}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="hd">High Definition (HD)</option>
                  <option value="sd">Standard Definition (SD)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Profile Data */}
          {profileData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-gray-200 rounded-lg p-6 mb-8"
            >
              <div className="flex flex-col md:flex-row gap-6">
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <img
                      src={profileData.profilePicUrl}
                      alt={`${profileData.username}'s profile`}
                      className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                    />
                    {profileData.isVerified && (
                      <div className="absolute -bottom-1 -right-1 bg-blue-500 rounded-full p-1">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">@{profileData.username}</h3>
                    {profileData.isVerified && (
                      <span className="text-blue-500 text-sm">✓ Verified</span>
                    )}
                    {profileData.isPrivate && (
                      <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                        Private
                      </span>
                    )}
                  </div>
                  
                  <p className="text-lg text-gray-700 mb-3">{profileData.fullName}</p>
                  
                  {profileData.biography && (
                    <p className="text-gray-600 mb-4">{profileData.biography}</p>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{formatNumber(profileData.posts)}</div>
                      <div className="text-sm text-gray-600">Posts</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{formatNumber(profileData.followers)}</div>
                      <div className="text-sm text-gray-600">Followers</div>
                    </div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-gray-900">{formatNumber(profileData.following)}</div>
                      <div className="text-sm text-gray-600">Following</div>
                    </div>
                  </div>

                  {/* Download Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={downloadProfilePicture}
                      className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download Profile Picture
                    </button>
                    <button
                      onClick={() => window.open(`https://instagram.com/${profileData.username}`, '_blank')}
                      className="flex items-center justify-center gap-2 bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View on Instagram
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-900 mb-1">Important Notice</h4>
                <p className="text-sm text-yellow-800">
                  This tool only works with public Instagram profiles. Please respect users' privacy and Instagram's terms of service. 
                  Downloaded images should only be used with proper permission from the profile owner.
                </p>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-3">How to Use</h4>
              <ul className="text-sm text-blue-800 space-y-2">
                <li>• Enter the Instagram username (without @)</li>
                <li>• Click "Search" to load profile information</li>
                <li>• Choose your preferred download quality</li>
                <li>• Click "Download Profile Picture" to save</li>
                <li>• The image will be saved to your downloads folder</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <h4 className="font-semibold text-green-900 mb-3">Features</h4>
              <ul className="text-sm text-green-800 space-y-2">
                <li>• Download profile pictures in HD quality</li>
                <li>• View public profile statistics</li>
                <li>• Check verification status</li>
                <li>• Support for all public profiles</li>
                <li>• Quick and easy interface</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">Privacy & Ethics</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• Only works with public profiles</li>
                <li>• Respects Instagram's privacy settings</li>
                <li>• Use downloaded images responsibly</li>
                <li>• Always obtain permission when needed</li>
                <li>• Follow Instagram's terms of service</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-lg p-6">
              <h4 className="font-semibold text-red-900 mb-3">Limitations</h4>
              <ul className="text-sm text-red-800 space-y-2">
                <li>• Cannot access private profiles</li>
                <li>• Profile must exist and be public</li>
                <li>• Download quality depends on original image</li>
                <li>• Some profiles may have download restrictions</li>
                <li>• Tool is for personal use only</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InstagramProfileDownloader;
